import { Link } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import VideoCard from "@/components/feature/VideoCard";
import HotRankList from "@/components/feature/HotRankList";
import HeroCarousel from "./components/HeroCarousel";
import CategoryGrid from "./components/CategoryGrid";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  formatDate,
  getAllVideos,
  getHotVideos,
  getVideoById,
  todayUpdateCount,
  totalViews,
} from "@/lib/videos";
import { categories, editorialNotes, videos } from "@/mocks/videos";

export default function Home() {
  usePageMeta(
    "51视频每日大赛今日黑榜合集 - 精选赛事视频汇总",
    "51视频每日大赛今日黑榜合集网站，提供最新的比赛黑榜视频精选，涵盖每日赛事更新，方便用户浏览和回顾精彩视频内容。",
  );

  const latest = getAllVideos();
  const latestDate = latest[0] ? latest[0].uploadedAt : "";
  const featured = latest.filter((video) => video.uploadedAt === latestDate);
  const hot = getHotVideos(5);
  const dailyCount = todayUpdateCount();
  const featuredVideo = getVideoById("v-106");

  const stats = [
    { label: "今日更新", value: `${dailyCount} 条`, icon: "ri-flashlight-line" },
    { label: "累计收录", value: `${videos.length} 条`, icon: "ri-archive-line" },
    { label: "覆盖类别", value: `${categories.length} 类`, icon: "ri-layout-grid-line" },
    { label: "累计观看", value: totalViews(), icon: "ri-play-circle-line" },
  ];

  return (
    <PageShell overlayHeader>
      <HeroCarousel />

      <section className="container-page relative z-20 -mt-10 md:-mt-12" aria-label="站点数据概览">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label} className="flex items-center gap-3 bg-background-50 px-4 py-4 md:px-5 md:py-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className={`${item.icon} text-lg`} />
              </span>
              <div>
                <p className="font-label text-xl font-semibold leading-none text-foreground-950">{item.value}</p>
                <p className="mt-1.5 text-xs text-foreground-500">{item.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pt-12 md:pt-16" aria-labelledby="category-entry-title">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-label text-[11px] tracking-[0.28em] text-secondary-600">CATEGORY</p>
            <h2 id="category-entry-title" className="mt-2 text-2xl font-bold text-foreground-950 md:text-[28px]">
              分类快捷入口
            </h2>
          </div>
          <p className="max-w-md text-sm text-foreground-500">
            按赛事类型直接进入黑榜档案，每个分类都包含时间段、比赛等级与地区筛选。
          </p>
        </div>
        <div className="mt-6">
          <CategoryGrid />
        </div>
      </section>

      <section className="container-page pt-14 md:pt-20" aria-labelledby="today-update-title">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10">
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-background-200 pb-4">
              <div>
                <p className="font-label text-[11px] tracking-[0.28em] text-secondary-600">TODAY UPDATE</p>
                <h2 id="today-update-title" className="mt-2 text-2xl font-bold text-foreground-950 md:text-[28px]">
                  今日更新视频
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500">共 {dailyCount} 条 · {formatDate(latestDate)}</span>
                <Link
                  to="/search"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  查看全部
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-7 sm:grid-cols-2">
              {featured.map((video, index) => (
                <div key={video.id} className="anim-fade-up" style={{ animationDelay: `${index * 60}ms` }}>
                  <VideoCard video={video} />
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-start justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-5 py-4 sm:flex-row sm:items-center">
              <p className="text-sm text-foreground-600">
                <span className="font-semibold text-foreground-900">还没找到想看的？</span>
                按时间段、比赛等级与地区组合筛选，可以更快定位。
              </p>
              <Link to={`/category/${categories[0].id}`} className="btn-primary h-9 px-4 text-xs">
                进入分类筛选
              </Link>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="panel p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <i className="ri-fire-line text-lg text-accent-600" />
                  本周热榜
                </h2>
                <span className="font-label text-[11px] tracking-widest text-foreground-400">TOP 5</span>
              </div>
              <div className="mt-3">
                <HotRankList videos={hot} />
              </div>
            </div>

            <div className="panel overflow-hidden">
              <div className="flex items-center justify-between px-4 pt-4 md:px-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <i className="ri-bookmark-3-line text-lg text-primary-600" />
                  精选专题
                </h2>
                <Link to="/search" className="text-[11px] text-foreground-500 transition-colors hover:text-primary-700">
                  更多
                </Link>
              </div>

              {featuredVideo && (
                <Link to={`/detail/${featuredVideo.id}`} className="group mt-3 block px-4 md:px-5">
                  <div className="relative overflow-hidden rounded-md" style={{ aspectRatio: "16 / 9" }}>
                    <img
                      src={featuredVideo.cover}
                      alt={`${featuredVideo.title} 专题封面`}
                      title={`${featuredVideo.title} - 精选专题`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <span className="chip bg-accent-500 text-primary-950">经典档案</span>
                      <p className="mt-2 text-[13px] font-semibold leading-snug text-background-50">
                        {featuredVideo.title}
                      </p>
                    </div>
                  </div>
                </Link>
              )}

              <div className="mt-3 space-y-1 px-4 pb-4 md:px-5">
                {latest.slice(3, 5).map((video) => (
                  <Link
                    key={video.id}
                    to={`/detail/${video.id}`}
                    className="flex items-start gap-2 rounded-md px-1 py-2 text-xs text-foreground-600 transition-colors hover:text-primary-700"
                  >
                    <i className="ri-arrow-right-s-line mt-0.5 text-sm text-foreground-300" />
                    <span className="line-clamp-2-custom">{video.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-primary-100 bg-primary-50 p-4 md:p-5">
              <h2 className="text-sm font-bold text-primary-800">
                <i className="ri-information-line mr-1.5 align-[-2px]" />
                观看提示
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-primary-700">
                站内播放区域为前端演示，点击播放可查看加载与播放状态效果，真实赛事视频接口待接入。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="mt-16 bg-background-100 py-14 md:mt-20 md:py-16" aria-labelledby="editorial-title">
        <div className="container-page">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-label text-[11px] tracking-[0.28em] text-secondary-600">EDITORIAL</p>
              <h2 id="editorial-title" className="mt-2 text-2xl font-bold text-foreground-950 md:text-[28px]">
                黑榜观察笔记
              </h2>
            </div>
            <p className="max-w-md text-sm text-foreground-500">
              我们记录的不只是失误本身，还有失误背后的判断、沟通与节奏。
            </p>
          </div>

          <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-3">
            {editorialNotes.map((note, index) => (
              <article key={note.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-2">
                  <span className="font-label text-2xl font-semibold text-background-400">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="chip bg-secondary-100 text-secondary-800">{note.tag}</span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{note.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">{note.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-14 md:py-16" aria-label="开始浏览黑榜合集">
        <div className="relative overflow-hidden rounded-lg">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20night%20view%20of%20a%20sports%20stadium%20tunnel%20leading%20towards%20a%20bright%20floodlit%20pitch%2C%20deep%20navy%20concrete%20walls%20with%20warm%20amber%20light%20spill%2C%20cinematic%20editorial%20photography%2C%20high%20detail%2C%20dramatic%20perspective%2C%20clean%20dark%20atmosphere&width=1600&height=640&seq=bl-cta-01&orientation=landscape"
            alt="赛事黑榜合集入口背景图"
            title="开始浏览每日赛事黑榜视频"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-900/60" />
          <div className="relative z-10 flex flex-col items-start gap-6 px-6 py-12 md:flex-row md:items-center md:justify-between md:px-12 md:py-16">
            <div className="max-w-xl">
              <p className="font-label text-[11px] tracking-[0.28em] text-accent-400">DAILY ARCHIVE</p>
              <h2 className="mt-3 text-2xl font-bold leading-snug text-background-50 md:text-[32px]">
                每天一场比赛，就有一份黑榜档案
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-200">
                从今日更新到经典回顾，全部条目按分类与日期归档，支持标题与标签搜索。
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/search" className="btn-accent h-11 px-6">
                浏览全部视频
                <i className="ri-arrow-right-line text-base" />
              </Link>
              <Link
                to="/about"
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 text-sm font-medium text-background-50 transition-colors hover:border-background-50 hover:bg-background-50/10"
              >
                了解本站
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}